import { useMemo } from "react";
import { useAppSelector } from "./useStore";
import { useAuth } from "./useAuth";
import {
  MODULES,
  resolveEnabledModules,
  type ModuleDef,
  type ModuleKey,
} from "../constants/modules";

/**
 * What this tenant may see.
 *
 * The single place the UI asks "is this module available?". Nav, routes and
 * landing screens all go through it, so they cannot drift apart and show a menu
 * entry that routes nowhere — or, worse, a route with no way back.
 *
 * NOT a security boundary. The backend module guard is
 * (`backend/middlewares/moduleGuard.js`), and it answers 404 for anything
 * switched off no matter what this hook says. What lives here is the
 * experience: never offer a door the API will refuse to open.
 *
 * The set is a `Set`, so `has()` is O(1) — it is called once per nav item on
 * every sidebar render and once per guarded route on every navigation.
 */
export const useModules = () => {
  const enabledList = useAppSelector((state) => state.modules.enabled);
  const { user } = useAuth();

  const enabled = useMemo(
    () => resolveEnabledModules(enabledList),
    [enabledList],
  );

  return useMemo(() => {
    const has = (key: ModuleKey) => enabled.has(key);

    /** Every module of one audience that this tenant has, in registry order. */
    const listFor = (audience: ModuleDef["audience"]) =>
      MODULES.filter((m) => m.audience === audience && enabled.has(m.key));

    /**
     * Where to send someone who asked for a module they cannot have.
     *
     * A redirect, never an error screen: an explicit "this module is disabled"
     * page would tell whoever probed the URL what other tenants are running,
     * and the backend deliberately answers 404 for the same reason. Landing
     * quietly on your own home screen is indistinguishable from mistyping.
     */
    const homePath = user?.role === "driver" ? "/driver-home" : "/dashboard";

    return { enabled, has, listFor, homePath };
  }, [enabled, user?.role]);
};
